import { useCallback, useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AuthContext } from "../../../contexts/AuthContext";
import Loading from "../components/Loading";
import Button from "../components/Button";
import BackButton from "../components/BackButton";
import { postsService } from "../../../services/api/postsService";
import { getCleanFileName } from "../../../utils/fileUtils";
import AuthorizationExportModal from "./components/AuthorizationExportModal";
import { useAuthorizationFileExport } from "./hooks/useAuthorizationFileExport";
import {
  getPostDetailPayload,
  getPostFileUrl,
} from "./utils/postResponseUtils";

const PostsDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token_client } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ name: "", txtUrl: "", fileUrl: "" });
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const { exporting, exportFile } = useAuthorizationFileExport({
    postId: id,
    postName: data.name,
    templateUrl: data.fileUrl,
    token: token_client,
  });

  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await postsService.get_by_id(id, token_client);
      const payload = getPostDetailPayload(response);

      let txtUrl = payload.txtUrl || "";
      let fileUrl = getPostFileUrl(payload);

      if (txtUrl && !fileUrl && !txtUrl.trim().startsWith("<")) {
        fileUrl = txtUrl;
        txtUrl = "";
      }

      setData({
        name: payload.name || "",
        txtUrl: txtUrl,
        fileUrl: fileUrl,
      });
    } catch (error) {
      toast.dismiss();
      toast.error(
        error.response?.data?.message || "Không thể tải dữ liệu bài viết",
      );
    } finally {
      setLoading(false);
    }
  }, [id, token_client]);

  useEffect(() => {
    if (!token_client) return;
    fetchPosts();
  }, [fetchPosts, token_client]);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loading />
      </div>
    );
  }

  const isPdf = (url) => {
    if (!url) return false;
    return url.split("?")[0].toLowerCase().endsWith(".pdf");
  };

  return (
    <div className="h-full overflow-auto bg-gray-50/50">
      <div className="mx-auto bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden min-h-full flex flex-col relative">
        <div className="px-8 py-6 border-b border-gray-100 bg-white sticky top-0 z-10 flex justify-between items-center group">
          <BackButton
            ariaLabel="Quay lại"
            onClick={() => navigate(-1)}
            title="Quay lại"
          />
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-bold text-[#0457C2] text-center uppercase tracking-tight">
              {data.name}
            </h1>
          </div>
          <div className="w-10"></div> {/* Spacer for symmetry */}
        </div>

        <div className="flex-1 px-8 py-8 md:px-12">
          {data.txtUrl && data.txtUrl !== "<p><br></p>" ? (
            <div
              className="prose max-w-none prose-blue prose-img:rounded-2xl prose-img:shadow-md prose-headings:text-[#1a1a1a] prose-p:text-gray-700 prose-p:leading-relaxed text-lg"
              dangerouslySetInnerHTML={{ __html: data.txtUrl }}
            />
          ) : (
            !data.fileUrl && (
              <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                <img
                  src="/icons/document-gray.svg"
                  alt="empty"
                  className="w-16 h-16 opacity-20 mb-4"
                />
                <p>Bài viết chưa có nội dung chi tiết.</p>
              </div>
            )
          )}

          {data.fileUrl && (
            <div
              className={`mt-10 pt-8 border-t border-gray-100 ${!data.txtUrl ? "mt-0 pt-0 border-t-0" : ""}`}
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <img src="/icons/folder.svg" alt="attach" className="w-5 h-5" />
                Dành cho bạn (File đính kèm)
              </h3>

              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 group hover:border-blue-300 transition-all">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4 overflow-hidden">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <img
                        src={
                          isPdf(data.fileUrl)
                            ? "/icons/pdf.svg"
                            : "/icons/file.svg"
                        }
                        alt="file type"
                        className="w-7 h-7"
                      />
                    </div>
                    <div className="overflow-hidden">
                      <p
                        className="font-medium text-gray-900 truncate"
                        title={getCleanFileName(data.fileUrl)}
                      >
                        {getCleanFileName(data.fileUrl)}
                      </p>

                      <p className="text-sm text-gray-500">
                        Nhấp để xem hoặc tải về máy
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setIsExportModalOpen(true)}
                      disabled={exporting}
                    >
                      Xuất file
                    </Button>
                    <Button type="button" variant="primary">
                      <a
                        href={data.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Xem tài liệu
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="px-8 py-4 bg-gray-50 border-t border-gray-100 text-sm text-gray-500 flex justify-between items-center">
          <span>Tài liệu nội bộ</span>
          <span>&copy; {new Date().getFullYear()} Công ty SHTT</span>
        </div>
      </div>
      <AuthorizationExportModal
        exporting={exporting}
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onSubmit={exportFile}
      />
    </div>
  );
};

export default PostsDetail;
